import { useState, useEffect, useCallback, useRef } from 'react';
import { PaymentCashServerProps } from '../../lib/typegen/payment-cash-server-props';
import GenericButton from '../button-generic';
import style from './style.module.scss';
import { useStore } from '../../lib/hooks/useStore';
import { devices } from '../../lib/devices/devices';
import cashImage from '../../assets/cash-acceptor.svg';
import BalancesKvpList from '../balances-kvp-list';
import { AppIconType } from '../../lib/typegen/app-icon-type';
import api from '../../lib/api/api';
import logger from '../../lib/helpers/logger';
import { ICashDenominations } from '../../lib/typegen/i-cash-denominations';
import { ViewState, PaymentError } from '../../lib/types/paymentCardState';
import MainMessageBox from '../../features/main-message-box/main-message-box';
import { CartItem } from '../../lib/types/CartItem';
import CurrencyDevice from '../../lib/devices/currency-acceptor/CurrencyDevice';
import { PaymentType } from '../../lib/typegen/payment-type';
import enumHelper from '../../lib/helpers/enums';
import { PayWithCashResponse } from '../../lib/typegen/pay-with-cash-response';
import MessageBoxSet from '../message-box-set';
import { useSessionCleanup } from '../../lib/hooks/useSessionCleanup';
import { CISConfigType } from '../../lib/typegen/cis-config-type';
import { PPConfigType } from '../../lib/typegen/pp-config-type';
import { getPPBehavior } from '../../lib/integrations';
import sqlite from '../../lib/helpers/sqlite';
import { ulid } from 'ulid';
import PendingCashPayment from '../../lib/types/PendingCashPayment';
import ApiCall, { ApiCallServiceType } from '../../lib/types/ApiCall';
import CashPaymentData from '../../lib/types/CashPaymentData';
import axios, { AxiosError } from 'axios';
import CashInsertModal from '../cash-insert-modal';
import { v4 as guid } from 'uuid';

type PaymentCashProps = PaymentCashServerProps;
let timer = 0;
const PAYMENT_CASH_REGISTRY_KEY = 'payment-cash';
const PROMPT_STUB_DENOMINATIONS = [1, 2, 5, 10, 20, 50, 100];

export default function PaymentCash({ insertCashLabel, warningLabel, amountEnteredLabel, amountOwedLabel, doneButton }: PaymentCashProps) {
	const {
		accountStore: { cartItems, setPaymentType, cartTotalAmount },
		kioskStore: { kioskId, companyId, sendCashAlert, paymentReferenceId },
		uiStore: { handleNavigation, setBackEnabled, setShowCart, setFooterEnabled, setCanCancel },
		sessionStore: { legacyId, sessionId, unregisterCleanup, touch },
		configStore: { getString, cisType, cashProcessorType, ppType, envDeviceOption, cashStubMode },
		bpkManagerStore: { isCashBoxFull },
	} = useStore();
	const [selectedItem, setSelectedItem] = useState<CartItem | null>(null);
	const [currentState, setCurrentState] = useState<ViewState>(ViewState.PAY);
	const [error, setError] = useState<PaymentError | null>(null);
	const [amountEntered, setAmountEntered] = useState<number>(0.0);
	const [isStacking, setIsStacking] = useState(false);
	const [currencyDevice, setCurrencyDevice] = useState<CurrencyDevice | null>(null);
	const [isCleanupRequired, setIsCleanupRequired] = useState<boolean>(false);
	const [denominations, setDenominations] = useState<ICashDenominations>({
		ones: 0,
		twos: 0,
		fives: 0,
		tens: 0,
		twenties: 0,
		fifties: 0,
		hundreds: 0,
		unknownCount: 0,
		unknownAmount: 0,
	});
	const [pendingCashPayment, setPendingCashPayment] = useState<PendingCashPayment | null>(null);
	const [isPromptModalOpen, setIsPromptModalOpen] = useState<boolean>(true);
	const isPaymentProcessing = useRef(false);
	const isPayGoCustomer = cisType === CISConfigType.PayGo || ppType === PPConfigType.PayGo;
	const isPromptStubMode = currencyDevice === devices.CurrencyDeviceStub && cashStubMode === 'prompt';

	// Modal-only handler for prompt-stub mode.  Mirrors the device-callback bill-insertion logic.
	const handleManualBillInserted = useCallback(
		async (amount: number) => {
			if (!pendingCashPayment) return;

			const denomMap: Record<number, keyof ICashDenominations> = {
				1: 'ones',
				2: 'twos',
				5: 'fives',
				10: 'tens',
				20: 'twenties',
				50: 'fifties',
				100: 'hundreds',
			};

			await sqlite.createBillInsertion({
				id: ulid().toUpperCase(),
				pendingCashPaymentId: pendingCashPayment.id,
				amount,
			});

			logger.logCashAcceptor.log({}, `Currency device reported amount: $${amount}`, amount);
			setAmountEntered((prevAmount) => prevAmount + amount);

			const key = denomMap[amount];
			if (key) {
				setDenominations((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
			} else {
				// Track unknown denominations
				setDenominations((prev) => ({
					...prev,
					unknownCount: (prev.unknownCount ?? 0) + 1,
					unknownAmount: (prev.unknownAmount ?? 0) + amount,
				}));
			}
		},
		[pendingCashPayment]
	);

	const handleCompletedPayment = useCallback(async () => {
		if (isPaymentProcessing.current) return;
		isPaymentProcessing.current = true;

		const item = cartItems[0];
		if (!item) return;

		let cashPaymentData: CashPaymentData | undefined;
		let paymentProviderResponse: PayWithCashResponse | undefined;
		try {
			setCanCancel(false);
			setCurrentState(ViewState.PROCESSING);
			currencyDevice?.endTransaction();

			cashPaymentData = new CashPaymentData({
				...pendingCashPayment!,
				amountToPay: amountEntered,
				isPrePay: isPayGoCustomer ? item.item.isPrePay : undefined,
				ones: denominations.ones,
				twos: denominations.twos,
				fives: denominations.fives,
				tens: denominations.tens,
				twenties: denominations.twenties,
				fifties: denominations.fifties,
				hundreds: denominations.hundreds,
				unknownCount: denominations.unknownCount,
				unknownAmount: denominations.unknownAmount,
				timestamp: new Date().toISOString(),
			});

			const result = await api.payments.cash(cashPaymentData.toPayWithCashRequest());
			paymentProviderResponse = result.data;
			if (result.status === 200) {
				const apiCall: ApiCall = {
					id: ulid().toUpperCase(),
					type: ApiCallServiceType.LegacyPostAcceptedPayment,
					requestData: JSON.stringify(await cashPaymentData.toLegacyPostAcceptedPaymentRequest(result.data?.kioskPaymentId)),
					timestamp: new Date(),
				};
				await sqlite.createApiCall(apiCall);
				await sqlite.deletePendingCashPayment(pendingCashPayment!.id);
			}

			logger.debug.log('Cash payment response:', paymentProviderResponse);
			if (doneButton.target) handleNavigation(doneButton.target);
			else handleNavigation('payment-complete');
		} catch (error) {
			let logMessage = 'Error processing cash payment.  Payment will be retried later:';
			if (axios.isAxiosError(error) && ((error as AxiosError).response?.status ?? 0) >= 500) {
				const apiCall: ApiCall = {
					id: ulid().toUpperCase(),
					type: ApiCallServiceType.LegacyPostRejectedPayment,
					requestData: JSON.stringify(await cashPaymentData!.toLegacyPostRejectedPaymentRequest()),
					timestamp: new Date(),
				};
				await sqlite.createApiCall(apiCall);
				await sqlite.deletePendingCashPayment(pendingCashPayment!.id);
				logMessage = 'Error processing cash payment.  Payment was rejected by the server:';
			}

			setError({ message: 'Cash payment error' });
			setCurrentState(ViewState.ERROR);
			sendCashAlert('CPFailure', 'Cash Payment Failure');
			logger.logCashPayment.error(
				structuredClone({
					paymentId: paymentProviderResponse?.paymentId ?? 'Unknown',
					customerName: selectedItem?.item.customerName ?? selectedItem?.customer?.name ?? '',
					cISConfig: cisType,
					pPConfig: ppType,
					referenceNumberValue: paymentProviderResponse?.referenceNumberValue ?? 'Unknown',
					referenceNumberLabel: paymentProviderResponse?.referenceNumberLabel ?? 'Unknown',
					amountToPay: selectedItem?.amountToPay ?? 0,
					ones: denominations.ones,
					twos: denominations.twos,
					fives: denominations.fives,
					tens: denominations.tens,
					twenties: denominations.twenties,
					fifties: denominations.fifties,
					hundreds: denominations.hundreds,
				}),
				logMessage,
				error
			);
		} finally {
			setIsCleanupRequired(false);
			unregisterCleanup(PAYMENT_CASH_REGISTRY_KEY);
			setCanCancel(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		currencyDevice,
		selectedItem,
		amountEntered,
		denominations.ones,
		denominations.twos,
		denominations.fives,
		denominations.tens,
		denominations.twenties,
		denominations.fifties,
		denominations.hundreds,
		denominations.unknownCount,
		denominations.unknownAmount,
		legacyId,
		companyId,
		kioskId,
		cisType,
		ppType,
		pendingCashPayment,
		setCanCancel,
		doneButton.target,
		handleNavigation,
		unregisterCleanup,
	]);

	useSessionCleanup(
		PAYMENT_CASH_REGISTRY_KEY,
		useCallback(async () => {
			if (!isCleanupRequired) return;
			logger.logGeneral.warn({}, 'Cleaning up incomplete cash payment session');
			currencyDevice?.endTransaction();
			await new Promise((resolve) => setTimeout(resolve, 1000));
		}, [currencyDevice, isCleanupRequired])
	);

	useEffect(() => setPaymentType(PaymentType.Cash), [setPaymentType]);

	useEffect(() => {
		if (amountEntered <= 0) return;

		const checkCashBoxFull = async () => {
			const currentCount =
				denominations.ones +
				denominations.twos +
				denominations.fives +
				denominations.tens +
				denominations.twenties +
				denominations.fifties +
				denominations.hundreds +
				(denominations.unknownCount ?? 0);

			const full = await isCashBoxFull(currentCount);
			if (full) {
				handleCompletedPayment();
				logger.logCashAcceptor.warn({}, 'Cash box is full');
				sendCashAlert('CBFull', 'Cash box is full');
				return;
			}

			if (amountEntered >= cartTotalAmount) {
				logger.logGeneral.log(
					{},
					`Amount entered ($${amountEntered}) meets or exceeds cart total ($${cartTotalAmount}), auto-completing payment`
				);
				handleCompletedPayment();
			}
		};

		clearTimeout(timer);
		checkCashBoxFull();
	}, [
		amountEntered,
		denominations.ones,
		denominations.twos,
		denominations.fives,
		denominations.tens,
		denominations.twenties,
		denominations.fifties,
		denominations.hundreds,
		denominations.unknownCount,
		isCashBoxFull,
		sendCashAlert,
		currencyDevice,
		handleCompletedPayment,
		cartTotalAmount,
	]);

	useEffect(() => {
		const handleDeviceFallback = () => {
			if (envDeviceOption !== 'with-fallback') return;

			// For the browser
			if (!window.KioCurrencyAcceptor) {
				setCurrencyDevice(devices.CurrencyDeviceStub);
				return;
			}

			// For Kioware when the device is not connected
			try {
				setCurrencyDevice(devices.currencyDevice);
				clearTimeout(timer);
				timer = setTimeout(() => setCurrencyDevice(devices.CurrencyDeviceStub), 15000);
			} catch (error) {
				clearTimeout(timer);
				logger.debug.log('Error initializing currency device, falling back to stub device:', error);
				setCurrencyDevice(devices.CurrencyDeviceStub);
			}
		};

		switch (envDeviceOption) {
			case 'always':
				setCurrencyDevice(devices.currencyDevice);
				break;
			case 'never':
				setCurrencyDevice(devices.CurrencyDeviceStub);
				break;
			case 'with-fallback':
				handleDeviceFallback();
				break;
			default:
				setCurrencyDevice(devices.currencyDevice);
		}
	}, [envDeviceOption]);

	useEffect(() => {
		if (!selectedItem) return;
		selectedItem.cashAmountPaid = amountEntered;
		touch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedItem, amountEntered]);

	// Cannot go back now that money has been entered.
	useEffect(() => {
		if (amountEntered > 0) {
			setIsCleanupRequired(true);
			setShowCart(false);
			setBackEnabled(false);
			setFooterEnabled(false);
		}
		return () => setFooterEnabled(true);
	}, [amountEntered, setBackEnabled, setFooterEnabled, setShowCart]);

	useEffect(() => {
		if (cartItems.length > 1) {
			logger.logPP.error({}, 'Paying for more than one account at a time is not supported', new Error('Multiple accounts in cart'));
			throw new Error('Paying for more than one account at a time is not supported');
		} else if (cartItems.length === 1) setSelectedItem(cartItems[0]);
	}, [cartItems, cartItems.length]);

	useEffect(() => {
		const initPendingCashPayment = async () => {
			if (pendingCashPayment !== null) return;

			const item = cartItems[0];
			if (!item || !legacyId || !sessionId || !selectedItem) return;

			logger.debug.warn('update the legacy kiosk id, paymentGuid, paymentGatewayExternalID, cisProvider, paymentGateway');
			const pcp: PendingCashPayment = {
				id: ulid().toUpperCase(),
				sessionId: sessionId!,
				companyId: companyId,
				kioskId: kioskId,
				customerName: item.item.customerName ?? selectedItem.customer?.name ?? '',
				utilityAccountNumber: selectedItem.id!,
				accountToken: item.item.accountToken ?? null,
				amountDue: selectedItem.amountToPay ?? 0,
				invoiceGuid: null,
				paymentTypeId: item.item.icCashPaymentId || null,
				legacyGuid: legacyId!,
				paymentGuid: guid(),
				cisId: selectedItem.id!,
				paymentGatewayId: selectedItem.item.itemNumber,
				paymentGatewayExternalId: getPPBehavior(ppType).getCashPaymentGatewayExternalId?.() ?? null,
				cisProviderName: enumHelper.getCisConfigTypeLabel(cisType),
				// Cash never touches a card gateway, so the processor is whoever actually took the money:
				// the CIS that receives the postback (CSA on a MyUsage kiosk, the CIS itself on Fiserv).
				paymentGatewayName: enumHelper.getCashProcessorLabel(cashProcessorType),
				group: item.item.group ?? null,
				classification: item.item.classification ?? null,
				paymentReference: paymentReferenceId,
			};

			if (ppType === PPConfigType.InvoiceCloudV1 || ppType === PPConfigType.InvoiceCloudV2) pcp.invoiceGuid = item.item.itemGuid || null;

			await sqlite.createPendingCashPayment(pcp);
			setPendingCashPayment(pcp);
		};

		initPendingCashPayment();
	}, [
		cartItems,
		legacyId,
		sessionId,
		ppType,
		selectedItem,
		pendingCashPayment,
		setPendingCashPayment,
		companyId,
		kioskId,
		cisType,
		cashProcessorType,
		paymentReferenceId,
	]);

	useEffect(() => {
		if (!pendingCashPayment) return;
		// In prompt-stub mode the modal drives bill insertion; no device transaction is needed.
		if (isPromptStubMode) return;

		const denomMap: Record<number, keyof ICashDenominations> = {
			1: 'ones',
			2: 'twos',
			5: 'fives',
			10: 'tens',
			20: 'twenties',
			50: 'fifties',
			100: 'hundreds',
		};

		currencyDevice?.beginTransaction(async (amount) => {
			await sqlite.createBillInsertion({
				id: ulid().toUpperCase(),
				pendingCashPaymentId: pendingCashPayment!.id,
				amount,
			});

			logger.logCashAcceptor.log({}, `Currency device reported amount: $${amount}`, amount);
			setAmountEntered((prevAmount) => prevAmount + amount);

			const key = denomMap[amount];
			if (key) {
				setDenominations((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
			} else {
				// Track unknown denominations
				setDenominations((prev) => ({
					...prev,
					unknownCount: (prev.unknownCount ?? 0) + 1,
					unknownAmount: (prev.unknownAmount ?? 0) + amount,
				}));
			}
		}, setIsStacking);

		return () => {
			currencyDevice?.endTransaction();
		};
	}, [currencyDevice, pendingCashPayment, setBackEnabled, isPromptStubMode]);

	const renderPayView = () => (
		<section className={style.section}>
			<img className={style.section__image} src={cashImage} alt='Insert Cash' />
			<MessageBoxSet
				className={style.section__message}
				infoText={getString(insertCashLabel)}
				warningText={getString(warningLabel ?? 'cash-payment-warning')}
			/>
			<BalancesKvpList
				className={style.section__list}
				kvpList={[
					{ label: getString(amountOwedLabel), value: selectedItem?.amountToPay ?? 0, forceRender: true },
					{ label: getString(amountEnteredLabel), value: amountEntered, forceRender: true },
				]}
			/>
			{amountEntered > 0 && (
				<GenericButton
					icon={AppIconType.AcceptSolid}
					className={style.section__button}
					innerText={getString(doneButton.label)}
					onClick={handleCompletedPayment}
					isGrowHorizontally={false}
					hasGlow={true}
					disabled={isStacking}
				/>
			)}
			{isPromptStubMode && isPromptModalOpen && pendingCashPayment && (
				<CashInsertModal
					denominations={PROMPT_STUB_DENOMINATIONS}
					runningTotal={amountEntered}
					targetAmount={cartTotalAmount}
					onInsert={handleManualBillInserted}
					onClose={() => setIsPromptModalOpen(false)}
				/>
			)}
		</section>
	);

	const renderProcessingView = () => (
		<section className={style.processing}>
			<div className={style.processing__spinner}></div>
			<h2 className={style.processing__title}>{getString('processing-payment')}</h2>
		</section>
	);

	const renderErrorView = () => (
		<section className={style.error}>
			<MainMessageBox icon={AppIconType.Error} message={error?.message ?? getString('cashErrorGeneric')} />
		</section>
	);

	const renderCurrentView = () => {
		switch (currentState) {
			case ViewState.PAY:
				return renderPayView();
			case ViewState.PROCESSING:
				return renderProcessingView();
			case ViewState.ERROR:
				return renderErrorView();
			default:
				return renderPayView();
		}
	};

	return <section className={style.section}>{renderCurrentView()}</section>;
}
